/**
 * Geolocation-based domain redirection utility
 * Redirects users to the appropriate domain based on their location
 */

import { getPrimaryHost } from "@/config/domains";

const GEO_CACHE_KEY = 'geo_country';
const GEO_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours (reduces ipapi.co calls, avoids 429)

interface GeoLocationData {
  country_code: string;
  country: string;
}

interface GeoCache {
  country_code: string;
  ts: number;
}

/**
 * Detect user's country using IP geolocation
 * Uses free IP geolocation API (ipapi.co). Result cached in sessionStorage to avoid 429.
 */
export const detectUserCountry = async (): Promise<string | null> => {
  if (typeof window === 'undefined') return null;

  try {
    const cached = sessionStorage.getItem(GEO_CACHE_KEY);
    if (cached) {
      const parsed: GeoCache = JSON.parse(cached);
      if (Date.now() - parsed.ts < GEO_CACHE_TTL_MS && parsed.country_code) {
        return parsed.country_code;
      }
    }
  } catch {
    // ignore cache parse errors
  }

  try {
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (response.status === 429) {
      // Rate limited: do not retry, use default (no redirect)
      return null;
    }

    if (!response.ok) {
      throw new Error('Failed to fetch geolocation');
    }

    const data: GeoLocationData = await response.json();
    const code = data.country_code || null;
    if (code) {
      try {
        sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify({ country_code: code, ts: Date.now() }));
      } catch {
        // ignore quota errors
      }
    }
    return code;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[GeoRedirect] Failed to detect country:', error);
    }
    return null;
  }
};

/**
 * Check if user has manually selected a domain preference
 */
export const hasUserSelectedDomain = (): boolean => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('domain_preference') !== null;
};

/**
 * Get user's domain preference
 */
export const getUserDomainPreference = (): 'pt' | 'com' | null => {
  if (typeof window === 'undefined') return null;
  const preference = localStorage.getItem('domain_preference');
  return preference === 'pt' || preference === 'com' ? preference : null;
};

/**
 * Set user's domain preference (when they manually change language)
 */
export const setUserDomainPreference = (domain: 'pt' | 'com'): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem('domain_preference', domain);
};

/**
 * Detect if the request is from a search engine crawler.
 * Crawlers must not be redirected so that .pt and .com can be indexed correctly.
 */
export const isCrawler = (): boolean => {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return false;
  const ua = navigator.userAgent.toLowerCase();
  const crawlers = [
    'googlebot', 'bingbot', 'slurp', 'duckduckbot', 'baiduspider',
    'yandexbot', 'facebookexternalhit', 'twitterbot', 'rogerbot', 'linkedinbot',
    'embedly', 'quora link preview', 'showyoubot', 'outbrain', 'pinterest',
    'slackbot', 'vkshare', 'w3c_validator', 'whatsapp', 'applebot', 'petalbot',
  ];
  return crawlers.some((c) => ua.includes(c));
};

/**
 * Check if user should be redirected based on geolocation
 * Returns the target domain or null if no redirect needed
 */
export const shouldRedirectToDomain = async (): Promise<'pt' | 'com' | null> => {
  // Fase 6: both TLDs default to EN — no geo-based TLD redirect.
  return null;
};

/**
 * Perform the redirect to the appropriate domain
 */
export const redirectToDomain = (domain: 'pt' | 'com'): void => {
  if (typeof window === 'undefined') return;

  const currentPath = window.location.pathname;
  const currentSearch = window.location.search;
  const currentHash = window.location.hash;
  
  const targetDomain = getPrimaryHost(domain);
  const fullUrl = `https://${targetDomain}${currentPath}${currentSearch}${currentHash}`;

  // Store preference to avoid redirect loops
  setUserDomainPreference(domain);

  // Perform redirect
  window.location.href = fullUrl;
};

